<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { performLogout, getCurrentUser, persistUser, isAuthenticated, getBillingStatus, isClientWarned, isClientSuspended, isClientBlocked, isImpersonated, persistBillingStatus, hasPermission, isModuleEnabled, getValidToken, shouldRefreshToken, hasRefreshableSession, silentRefresh } from './services/authService'
import { disconnectClient as disconnectCentrifugo } from './services/centrifugoService'
import { t, isI18nReady } from './services/i18nService'
import { fetchTenantLogo, fetchUserProfile, fetchBillingStatusInfo, fetchSession, reportVisit } from './services/portalService'
import { fetchBanner, markNotificationRead } from './services/billingService'
import UserSettingsModal from './components/UserSettingsModal.vue'
import NotificationBell from './components/NotificationBell.vue'
import WarningBanner from './components/WarningBanner.vue'
import NotificationModal from './components/NotificationModal.vue'
import CampaignModal from './components/CampaignModal.vue'
import { fetchCampaignPopup } from './services/campaignService'
import { HelpPanel, useHelp } from './help'
import { previewTheme, resolveTheme, subscribe as subscribeTheme } from './services/themeService'
import { getNavLayout, setNavLayout, subscribe as subscribeNavLayout } from './services/navLayoutService'
import { hasUnreadNews, checkLatestNews } from './services/newsService'
import PwaInstallPrompt from './components/PwaInstallPrompt.vue'
import AppPushOffer from './components/AppPushOffer.vue'
import IosInstallGuide from './components/IosInstallGuide.vue'
import OfflineBanner from './components/OfflineBanner.vue'
import DriverChatWindow from './components/DriverChatWindow.vue'
import { startChatPolling, stopChatPolling, useDriverChat } from './composables/useDriverChat'
import ClientLayoutRouter from './layouts/ClientLayoutRouter.vue'
import { getBrand } from './utils/branding'
import { useAlarmState } from './composables/useAlarmState'
import { useSupportUnread } from './composables/useSupportUnread'
// White label: реактивний бренд партнерського домену (логотип у шапці до логіна)
import { wlBrandRef } from './services/brandingService'

const router = useRouter()
const route = useRoute()
// Глобальний стан тривог (звук + системні сповіщення на всіх сторінках)
const alarmState = useAlarmState()
// Бейдж непрочитаних повідомлень підтримки на пункті «Заявки»
const {
  unreadCount: supportUnreadCount,
  refresh: refreshSupportUnread,
  start: startSupportUnread,
  stop: stopSupportUnread,
} = useSupportUnread()
// Стан завантаження i18n - показуємо loader поки словник не завантажено
const i18nLoaded = ref(isI18nReady())
const authenticated = ref(isAuthenticated())
// Бейдж непрочитаних повідомлень: окремо для «Водіїв» і «Персоналу» —
// це різні пункти меню, хоч люди і лежать в одній таблиці.
const { unreadByKind: chatUnreadByKind } = useDriverChat()
// Скільки непрочитаних показати біля пункту меню (0 — бейджа немає)
function navUnread(link) {
  if (link.to === '/drivers') return chatUnreadByKind.value.driver || 0
  if (link.to === '/personnel') return chatUnreadByKind.value.personnel || 0
  return 0
}
const currentUser = ref(getCurrentUser())
const isMenuOpen = ref(false)
const isUserMenuOpen = ref(false)
const userMenuRef = ref(null)
const isSettingsOpen = ref(false)
const tenantLogo = ref(null)
const userAvatar = ref(null)
const banners = ref([])
const dismissedBannerIds = ref(new Set())
const selectedBannerForModal = ref(null)
const currentTime = ref('')

// Поточна застосована тема (light|dark). Кнопка-перемикач у шапці
// перемикає її в межах сеансу, без збереження в localStorage.
const currentTheme = ref(resolveTheme())
let unsubscribeTheme = null
const toggleTheme = () => {
  previewTheme(currentTheme.value === 'dark' ? 'light' : 'dark')
}

// Режим главного меню (desktop): topbar | side | side-icons. В шаблоне нужен для
// title-подсказок в узком режиме, кнопки свернуть/развернуть и якоря тура.
const navLayout = ref(getNavLayout())
let unsubscribeNavLayout = null
const navIconsOnly = computed(() => navLayout.value === 'side-icons')
const toggleSideNav = () => {
  setNavLayout(navIconsOnly.value ? 'side' : 'side-icons')
}
const isScrolled = ref(false)
// Гістерезис для запобігання дрожанню меню при скролі
const scrollThresholdDown = 50  // Поріг для активації компактного режиму
const scrollThresholdUp = 30    // Поріг для деактивації (менший для гістерезису)

// Перевірка доступу до білінгу для субкористувачів (використовується в баннерах та оповіщеннях)
// Використовуємо currentUser.value для реактивності після логіну
const hasBillingAccess = () => {
  const user = currentUser.value
  if (!user?.isSubUser) return true
  return hasPermission('canViewBilling')
}

const hasMessagesAccess = () => {
  const user = currentUser.value
  if (!user?.isSubUser) return true
  return hasPermission('canViewMessages')
}

// Заявки дилеру: власник завжди, співробітник — з правом canManageRequests
const hasRequestsAccess = () => {
  const user = currentUser.value
  if (!user?.isSubUser) return true
  return hasPermission('canManageRequests')
}

// Фильтруем баннеры - не показываем dismissed і не показуємо субкористувачам без canViewBilling
const visibleBanners = computed(() => {
  if (isLoginPage.value || isBlockedPage.value) return []
  if (!hasBillingAccess()) return []
  return banners.value.filter(b => !dismissedBannerIds.value.has(b.id))
})

// Показываем баннер статуса биллинга (warned/suspended) если нет API баннеров
const showBillingStatusBanner = computed(() => {
  if (isLoginPage.value || isBlockedPage.value) return false
  if (!hasBillingAccess()) return false
  if (visibleBanners.value.length > 0) return false
  return isClientWarned() || isClientSuspended()
})

// Вычисляем variant для баннера по типу уведомления
const getBannerVariant = (type) => {
  const typeMap = {
    block_notice: 'error',
    warning: 'warning',
    payment_overdue: 'warning',
    suspension_notice: 'suspended',
    invoice_issued: 'info',
    payment_reminder: 'info',
  }
  return typeMap[type] || 'warning'
}

const billingStatusBannerVariant = computed(() => {
  if (isClientSuspended()) return 'suspended'
  if (isClientWarned()) return 'warning'
  return 'warning'
})

const billingStatusBannerTitle = computed(() => {
  if (isClientSuspended()) return t('billing.suspended.bannerTitle')
  if (isClientWarned()) return t('billing.warned.bannerTitle')
  return ''
})

const billingStatusBannerMessage = computed(() => {
  if (isClientSuspended()) return t('billing.suspended.bannerMessage')
  if (isClientWarned()) return t('billing.warned.bannerMessage')
  return ''
})

// Навігаційні посилання з урахуванням прав субкористувача
// permission - ключ дозволу для субкористувачів (якщо не вказано - доступно всім)
// icon - Material Symbol: показывается в мобильной шторке и в боковых режимах меню
// (side / side-icons), в верхнем меню пункты остаются текстовыми.
const allNavLinks = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: 'dashboard', module: 'dashboard' },
  { to: '/devices', labelKey: 'nav.devices', icon: 'directions_car', permission: 'canViewDevices' },
  { to: '/reports', labelKey: 'nav.reports', icon: 'description', permission: 'canRequestReports', module: 'reports' },
  { to: '/regtech', labelKey: 'nav.regtech', icon: 'toll', permission: 'canViewRegtech' },
  { to: '/geozones', labelKey: 'nav.geozones', icon: 'polyline', permission: 'canViewGeozones', module: 'geozones' },
  { to: '/maintenance', labelKey: 'nav.maintenance', icon: 'build', permission: 'canViewMaintenance', module: 'maintenance' },
  // Завдання: платний модуль; диспетчерська сторінка і кабінет виконавця
  { to: '/tasks', labelKey: 'nav.tasks', icon: 'route', permission: 'canViewTasks', module: 'tasks' },
  { to: '/my-tasks', labelKey: 'nav.myTasks', icon: 'checklist', permission: 'canExecuteTasks', module: 'tasks', subUserOnly: true },
  { to: '/drivers', labelKey: 'nav.drivers', icon: 'badge', permission: 'canViewDrivers' },
  // Персонал: платный модуль, включается дилером на клиента
  { to: '/personnel', labelKey: 'nav.personnel', icon: 'groups', permission: 'canViewPersonnel', module: 'personnel' },
  // Тахограф: платный модуль, вивантаження файлів DDD по машинах і водіях
  { to: '/tacho', labelKey: 'nav.tacho', icon: 'timer', module: 'tacho' },
  { to: '/trailers', labelKey: 'nav.trailers', icon: 'rv_hookup', permission: 'canViewTrailers' },
  // АЗС: платний модуль (дилер вмикає клієнтам із заправками), лише з правом «АЗС»
  { to: '/fuel-stations', labelKey: 'nav.fuelStations', icon: 'local_gas_station', permission: 'canSeeStation', module: 'stationcards' },
  // Генератори: платний модуль, лише з правом «Генератори»
  { to: '/generators', labelKey: 'nav.generators', icon: 'bolt', permission: 'canSeeGenerators', module: 'generators' },
  { to: '/sub-users', labelKey: 'nav.subUsers', icon: 'manage_accounts', hideForSubUser: true, module: 'subusers' },
  { to: '/sharing', labelKey: 'nav.sharing', icon: 'share', hideForSubUser: true, module: 'sharing' },
]

const navLinks = computed(() => {
  // Фільтруємо навігацію з урахуванням відключених модулів та прав субкористувачів
  return allNavLinks.filter(link => {
    if (link.hideForSubUser && currentUser.value?.isSubUser) return false
    // subUserOnly: «Мої завдання» є лише у виконавця-субкористувача; власник — диспетчер
    if (link.subUserOnly && !currentUser.value?.isSubUser) return false
    // Модулі без окремого permission (dashboard, reports, geozones, maintenance, subusers, sharing)
    // гейтяться через isModuleEnabled; regtech/drivers/trailers вже гейтяться через permission.
    if (link.module && !isModuleEnabled(link.module)) return false
    if (link.permission) return hasPermission(link.permission)
    return true
  })
})

// Верхнее меню (desktop): при всех включённых модулях пункты не влезают в шапку,
// колокольчик и меню пользователя уезжают за край. Сколько пунктов влезает, считаем
// по скрытому ряду .nav-measure со всеми пунктами, остальные уходят в «Ещё».
const topBarRef = ref(null)
const brandBlockRef = ref(null)
const topBarActionsRef = ref(null)
const navDesktopRef = ref(null)
const navMeasureRef = ref(null)
const navMoreRef = ref(null)
const navFitCount = ref(Infinity)
const isNavMoreOpen = ref(false)
const visibleNavLinks = computed(() => navLinks.value.slice(0, navFitCount.value))
const overflowNavLinks = computed(() => navLinks.value.slice(navFitCount.value))
// Текущая страница спрятана в «Ещё» - подсвечиваем саму кнопку, как активный пункт
const isNavMoreActive = computed(() =>
  overflowNavLinks.value.some(link => route.path === link.to || route.path.startsWith(`${link.to}/`))
)
const navMoreUnread = computed(() => overflowNavLinks.value.reduce((sum, link) => sum + navUnread(link), 0))

const measureNavFit = () => {
  const bar = topBarRef.value
  const actions = topBarActionsRef.value
  const nav = navDesktopRef.value
  const measure = navMeasureRef.value
  // Мобильный и боковые режимы: верхнего меню нет, считать нечего
  if (!bar || !actions || !nav || !measure || !actions.offsetWidth) {
    navFitCount.value = Infinity
    return
  }
  const barStyle = getComputedStyle(bar)
  const navStyle = getComputedStyle(nav)
  const gap = parseFloat(navStyle.columnGap) || 0
  // Часы, тема, колокольчик и меню пользователя: ширина actions без самого меню
  // от числа пунктов не зависит, поэтому пересчёт не раскачивается
  const rest = actions.offsetWidth - nav.offsetWidth
  // 24px запаса: подложка .top-bar__actions::before выступает влево на 1.25rem
  const available = bar.clientWidth
    - parseFloat(barStyle.paddingLeft) - parseFloat(barStyle.paddingRight)
    - (brandBlockRef.value?.offsetWidth || 0) - (parseFloat(barStyle.columnGap) || 0)
    - rest - parseFloat(navStyle.paddingLeft) - parseFloat(navStyle.paddingRight) - 24
  // Последний элемент ряда - образец кнопки «Ещё»
  const widths = Array.from(measure.children).map(el => el.getBoundingClientRect().width)
  const moreWidth = widths.pop()
  const total = widths.reduce((sum, w) => sum + w, 0) + gap * Math.max(widths.length - 1, 0)
  if (total <= available) {
    navFitCount.value = Infinity
    return
  }
  let used = moreWidth
  let count = 0
  for (const w of widths) {
    if (used + gap + w > available) break
    used += gap + w
    count++
  }
  navFitCount.value = count
}

// Пересчёт в следующем кадре: менять разметку прямо в колбэке ResizeObserver нельзя
// (ошибка «ResizeObserver loop» в консоли и Sentry)
let navResizeObserver = null
let navMeasureFrame = 0
const scheduleNavMeasure = () => {
  if (navMeasureFrame) return
  navMeasureFrame = requestAnimationFrame(() => {
    navMeasureFrame = 0
    measureNavFit()
  })
}
// Шапка, логотип, actions и ряд-образец появляются и пропадают по v-if (логин, блокировка,
// загрузка словаря) - переподписываемся на каждый новый элемент. Ширина окна, шрифт,
// язык, крупный текст, компактная шапка при скролле и часы меняют размер одного из них.
watch([topBarRef, brandBlockRef, topBarActionsRef, navMeasureRef], (els) => {
  if (typeof ResizeObserver === 'undefined') return
  if (!navResizeObserver) navResizeObserver = new ResizeObserver(scheduleNavMeasure)
  navResizeObserver.disconnect()
  els.forEach(el => el && navResizeObserver.observe(el))
  scheduleNavMeasure()
}, { flush: 'post' })

const toggleNavMore = () => {
  isNavMoreOpen.value = !isNavMoreOpen.value
  if (isNavMoreOpen.value) isUserMenuOpen.value = false
}

const closeNavMore = () => {
  isNavMoreOpen.value = false
}

// Всё снова влезло - закрываем список, чтобы он не всплыл сам при следующем сужении
watch(() => overflowNavLinks.value.length, (n) => {
  if (!n) closeNavMore()
})

let removeAfterEachHook

const refreshAuthState = () => {
  // Считываем токен и профиль из localStorage, чтобы шапка всегда была синхронизирована.
  authenticated.value = isAuthenticated()
  currentUser.value = getCurrentUser()
}

// Вхід → перевіряємо, чи є нова новина «Що нового» (крапка на аватарі і в меню)
watch(authenticated, (isAuth) => {
  if (isAuth) checkLatestNews()
}, { immediate: true })

// Логін/логаут → одразу звіряємо бейдж підтримки (без токена скинеться в 0)
watch(authenticated, () => {
  refreshSupportUnread(true)
})

const loadBranding = async () => {
  // Загружаем аватар пользователя и логотип tenant для брендинга.
  // На публичной странице шаринга НИКАКИЕ клиентские API дёргать нельзя — иначе
  // утекают данные дилера/пользователя (тут токен может быть от текущей сессии
  // владельца в другой вкладке) и шапка в любом случае скрыта.
  if (isPublicPage.value || !isAuthenticated()) {
    tenantLogo.value = null
    userAvatar.value = null
    return
  }
  try {
    const [logoResponse, profileResponse] = await Promise.all([
      fetchTenantLogo().catch(() => null),
      fetchUserProfile().catch(() => null),
    ])
    tenantLogo.value = logoResponse?.logo || null
    userAvatar.value = profileResponse?.user?.profile?.avatar || null
  } catch {
    tenantLogo.value = null
    userAvatar.value = null
  }
}

const userAvatarUrl = computed(() => {
  if (!userAvatar.value?.base64 || !userAvatar.value?.mimeType) {
    return null
  }
  return `data:${userAvatar.value.mimeType};base64,${userAvatar.value.base64}`
})

const tenantLogoUrl = computed(() => {
  if (!tenantLogo.value?.base64 || !tenantLogo.value?.mimeType) {
    return null
  }
  return `data:${tenantLogo.value.mimeType};base64,${tenantLogo.value.base64}`
})

// Стандартний логотип (fallback якщо у дилера немає кастомного).
// Вибір залежить від бренду домена: на client.cloudgps.online (і dev/інших нових
// хостах) — cloudGPS PNG; на legacy globus.cloudgps.online — старий SVG.
// White label: на партнерському домені — логотип партнера з /api/branding/logo
// (реактивно через wlBrandRef, бо бренд може приїхати після mount).
const defaultLogoUrl = computed(() => wlBrandRef.value?.logo || getBrand().logo)

// Прапор: показуємо саме дефолтний cloudGPS-лого (не tenant-кастом і не WL).
// CSS-модифікатор брендового кропу (квадратний WebP) — щоб не давити 32px-аем,
// який підбирався під широкий Globus-SVG.
const isDefaultCloudgpsLogo = computed(() => !tenantLogoUrl.value && !wlBrandRef.value && getBrand().id === 'cloudgps')

// Логотип дилера (или дефолтный если нет)
const dealerLogoUrl = computed(() => {
  return tenantLogoUrl.value || defaultLogoUrl.value
})

// brandImageUrl оставляем для обратной совместимости
const brandImageUrl = computed(() => {
  return dealerLogoUrl.value
})

const handleLogout = async () => {
  // Полностью выходим из портала: відкликаємо refresh-токен на бекенді + чистимо локальний стан
  disconnectCentrifugo()
  await performLogout()
  refreshAuthState()
  // Сбрасываем брендинг предыдущего пользователя
  tenantLogo.value = null
  userAvatar.value = null
  router.push({ name: 'login' })
  isSettingsOpen.value = false
  // Закрываем выпадающее меню, чтобы оно не осталось открытым после выхода.
  closeUserMenu()
}

const toggleMenu = () => {
  // Мобильное меню нужно открывать/закрывать по тапу.
  isMenuOpen.value = !isMenuOpen.value
  // Оповещаем другие компоненты об открытии sidebar для закрытия popup'ов.
  if (isMenuOpen.value) {
    window.dispatchEvent(new Event('sidebar-opened'))
  }
}

const closeMenu = () => {
  // При смене маршрута или клике по подложке прячем меню.
  isMenuOpen.value = false
}

// Повторный клик по активной ссылке навигации — отправляем событие для сброса состояния страницы
const handleNavClick = (link, event) => {
  if (route.path === link.to) {
    window.dispatchEvent(new Event('nav-reclick'))
  }
}

const toggleUserMenu = () => {
  // Один клик открывает контекст пользователя, чтобы не занимать место в хедере.
  isUserMenuOpen.value = !isUserMenuOpen.value
  if (isUserMenuOpen.value) closeNavMore()
}

const closeUserMenu = () => {
  // Убираем меню при клике вне области, во избежание подвисаний.
  isUserMenuOpen.value = false
}

const openSettings = () => {
  // Закрываем меню и показываем модалку с настройками, чтобы не дублировать фокус.
  closeUserMenu()
  isSettingsOpen.value = true
}

const openSettingsFromSidebar = () => {
  // Закрываем боковое меню и открываем настройки.
  closeMenu()
  isSettingsOpen.value = true
}

const openBilling = () => {
  closeUserMenu()
  router.push({ name: 'billing' })
}

const openBillingFromSidebar = () => {
  closeMenu()
  router.push({ name: 'billing' })
}

const openNotifications = () => {
  closeUserMenu()
  router.push({ name: 'notifications' })
}

const openNotificationsFromSidebar = () => {
  closeMenu()
  router.push({ name: 'notifications' })
}

// Є непрочитана відповідь підтримки → відкриваємо одразу вкладку «Техпідтримка»
const requestsRoute = () => (
  supportUnreadCount.value > 0 ? { name: 'requests', query: { tab: 'support' } } : { name: 'requests' }
)

const openRequests = () => {
  closeUserMenu()
  router.push(requestsRoute())
}

const openRequestsFromSidebar = () => {
  closeMenu()
  router.push(requestsRoute())
}

const closeSettings = () => {
  isSettingsOpen.value = false
}

const openSettingsForEmail = () => {
  isSettingsOpen.value = true
}

const onProfileUpdated = () => {
  // Обновляем состояние авторизации и перезагружаем брендинг (аватар мог измениться).
  refreshAuthState()
  loadBranding()
}

const handleDocumentClick = (event) => {
  if (
    isUserMenuOpen.value &&
    userMenuRef.value &&
    !userMenuRef.value.contains(event.target)
  ) {
    closeUserMenu()
  }
  if (
    isNavMoreOpen.value &&
    navMoreRef.value &&
    !navMoreRef.value.contains(event.target)
  ) {
    closeNavMore()
  }
}

const isLoginPage = computed(() => route.name === 'login' || route.name === 'migration-welcome')
// Публичные страницы (например, /share/{token}) показываются без шапки и сайдбара портала.
const isPublicPage = computed(() => !!route.meta?.publicPage)
const isBlockedPage = computed(() => route.name === 'blocked')

// Десктоп (>= 960px, тот же порог, что в CSS): только там есть боковые режимы меню,
// на мобильном всегда бургер + шторка, какой бы режим ни был сохранён.
const desktopQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  ? window.matchMedia('(min-width: 960px)')
  : null
const isDesktop = ref(desktopQuery ? desktopQuery.matches : true)
const onDesktopChange = (e) => {
  isDesktop.value = e.matches
}
// Боковое меню активно: режим side/side-icons, десктоп и страница с навигацией.
// Ставит класс .app-shell--side-nav (все CSS боковых режимов) и решает, где рендерить
// часы/тему/колокольчик/меню пользователя - в шапке или внизу колонки (Teleport).
const sideNavActive = computed(() =>
  isDesktop.value && navLayout.value !== 'topbar' && !isLoginPage.value && !isBlockedPage.value && !isPublicPage.value
)
const sideNavIconsOnly = computed(() => sideNavActive.value && navIconsOnly.value)

// Баннер «Email не заповнено» — показуємо після логіну, поки email порожній
const emailBannerDismissed = ref(false)
let emailPollInterval = null

const showEmailBanner = computed(() => {
  if (isLoginPage.value || isBlockedPage.value || isPublicPage.value || emailBannerDismissed.value) return false
  const user = currentUser.value
  // Баннер показуємо тільки власнику аккаунта, не суб-користувачам
  return user && !user.isSubUser && !user.email
})

// Оновлення email з сервера — використовується і полінгом, і при ?emailConfirmed=1
async function refreshEmailFromServer() {
  try {
    const data = await fetchSession()
    if (data?.user?.email) {
      const user = getCurrentUser()
      if (user) {
        user.email = data.user.email
        persistUser(user)
        currentUser.value = { ...user }
      }
      return true
    }
  } catch { /* тиха обробка */ }
  return false
}

// Полінг: перевіряємо чи email підтверджено (користувач підтверджує в іншій вкладці)
function startEmailPoll() {
  stopEmailPoll()
  emailPollInterval = setInterval(async () => {
    if (!showEmailBanner.value) { stopEmailPoll(); return }
    const confirmed = await refreshEmailFromServer()
    if (confirmed) stopEmailPoll()
  }, 30000)
}

function stopEmailPoll() {
  if (emailPollInterval) {
    clearInterval(emailPollInterval)
    emailPollInterval = null
  }
}

// При поверненні фокусу — перевіряємо і перезапускаємо полл; при втраті — зупиняємо
function onVisibilityChange() {
  if (!showEmailBanner.value) return
  if (document.hidden) {
    stopEmailPoll()
  } else {
    refreshEmailFromServer()
    startEmailPoll()
  }
}

watch(showEmailBanner, (show) => {
  if (show) {
    startEmailPoll()
    document.addEventListener('visibilitychange', onVisibilityChange)
  } else {
    stopEmailPoll()
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}, { immediate: true })

// Кампании дилера (пропозиції): одно окно на всё приложение. Открывается при входе (самая
// свежая непросмотренная), из колокольчика (оба экземпляра шлют openCampaign) и по ссылке
// из push (?campaign=ID на любой странице). Только владельцу аккаунта
const campaignModal = ref({ open: false, id: null, preloaded: null, fromPopup: false })

const openCampaign = (id) => {
  if (!id) return
  campaignModal.value = { open: true, id, preloaded: null, fromPopup: false }
}

const closeCampaign = () => {
  campaignModal.value = { open: false, id: null, preloaded: null, fromPopup: false }
}

const checkCampaignPopup = async () => {
  if (!authenticated.value || currentUser.value?.isSubUser) return
  if (isPublicPage.value || isLoginPage.value || isBlockedPage.value || campaignModal.value.open) return
  // Новому клиенту сначала обучающий тур: окно поверх него мешало бы
  if (shouldShowOnboarding()) return
  try {
    const res = await fetchCampaignPopup()
    if (res?.campaign && !campaignModal.value.open) {
      campaignModal.value = { open: true, id: res.campaign.id, preloaded: res.campaign, fromPopup: true }
    }
  } catch {
    // Окно с пропозицией не критично: ошибку не показываем
  }
}

// Вход (и старт приложения с живой сессией): окно с пропозицией после того, как
// интерфейс прогрузился
watch(authenticated, (isAuth) => {
  if (isAuth) setTimeout(checkCampaignPopup, 2000)
}, { immediate: true })

watch(() => route.query.campaign, (val) => {
  const id = Number(val)
  if (!val || !Number.isInteger(id) || id <= 0) return
  if (authenticated.value && !currentUser.value?.isSubUser) openCampaign(id)
  // Прибираємо query-параметр з URL
  router.replace({ ...route, query: { ...route.query, campaign: undefined } })
}, { immediate: true })

// Якщо прийшли з редіректу після підтвердження email — перевіряємо негайно
watch(() => route.query.emailConfirmed, async (val) => {
  if (val) {
    await refreshEmailFromServer()
    // Прибираємо query-параметр з URL
    router.replace({ ...route, query: { ...route.query, emailConfirmed: undefined } })
  }
}, { immediate: true })

// Система справки - инициализация и автозапуск onboarding
const { runOnboarding, shouldShowOnboarding, completeOnboarding, openPanel } = useHelp()

// Открыть справку из меню пользователя
const openHelp = () => {
  closeUserMenu()
  openPanel()
}

// Открыть справку из бокового меню (mobile)
const openHelpFromSidebar = () => {
  closeMenu()
  openPanel()
}

// «Що нового»: стрічка новин порталу (/news); крапка на аватарі = є новина, якої ще не бачили
const openWhatsNew = () => {
  closeUserMenu()
  router.push({ name: 'whats-new' })
}

const openWhatsNewFromSidebar = () => {
  closeMenu()
  router.push({ name: 'whats-new' })
}

// Запускаем onboarding при первом входе (с небольшой задержкой для загрузки интерфейса)
const startOnboardingIfNeeded = () => {
  if (shouldShowOnboarding() && authenticated.value && !isLoginPage.value && !isBlockedPage.value) {
    setTimeout(() => {
      runOnboarding()
      completeOnboarding()
    }, 1500)
  }
}

const loadBillingStatus = async () => {
  // Загружаем актуальный статус биллинга и баннеры.
  // На публичной странице шаринга НЕЛЬЗЯ — это утечка данных дилера.
  if (isPublicPage.value || !isAuthenticated()) {
    banners.value = []
    return
  }
  try {
    const [statusResponse, bannerResponse] = await Promise.all([
      fetchBillingStatusInfo().catch((e) => { console.error('fetchBillingStatusInfo error:', e); return null }),
      fetchBanner().catch((e) => { console.error('fetchBanner error:', e); return null }),
    ])
    if (statusResponse?.billingStatus) {
      const prevStatus = getBillingStatus()?.status
      const newStatus = statusResponse.billingStatus

      persistBillingStatus({
        status: newStatus,
        blockReason: statusResponse.blockReason || null,
        blockedAt: statusResponse.blockedAt || null,
      })

      // Если статус изменился на blocked - редиректим на страницу блокировки.
      // Impersonate-сессию не запираем: админ зашёл смотреть портал заблокированного клиента.
      if (newStatus === 'blocked' && prevStatus !== 'blocked' && route.name !== 'blocked' && !isImpersonated()) {
        router.push({ name: 'blocked' })
      }
      // Если статус изменился с blocked на другой - редиректим на dashboard
      if (prevStatus === 'blocked' && newStatus !== 'blocked' && route.name === 'blocked') {
        router.push({ name: 'dashboard' })
      }
    }
    // API возвращает { banners: [...] } - массив баннеров
    const bannersData = bannerResponse?.banners || []
    banners.value = Array.isArray(bannersData) ? bannersData : []
  } catch (e) {
    console.error('loadBillingStatus error:', e)
    banners.value = []
  }
}

// Клиент открыл баннер или закрыл его крестиком: он его видел, в админке это «Прочитано».
// В режиме «Увійти як клієнт» не отмечаем: баннер смотрит админ, а не клиент
const markBannerRead = (banner) => {
  if (!banner || banner.isRead || isImpersonated()) return
  banner.isRead = true
  markNotificationRead(banner.id).catch(() => { banner.isRead = false })
}

const dismissBanner = (bannerId) => {
  // Скрываем баннер только для текущей сессии
  // При перезагрузке страницы баннер появится снова, пока админ не выключит show_as_banner
  dismissedBannerIds.value.add(bannerId)
  markBannerRead(banners.value.find(b => b.id === bannerId))
}

const openBannerModal = (banner) => {
  selectedBannerForModal.value = banner
  markBannerRead(banner)
}

const closeBannerModal = () => {
  selectedBannerForModal.value = null
}

let billingPollInterval = null
let timeInterval = null
let i18nCheckInterval = null
let stopSplitLayoutWatcher = null
let stopSideNavWatcher = null
let iosRepaintHandler = null
let iosPageshowHandler = null
let tokenResumeHandler = null
let tokenResumePageshowHandler = null

// Вкладка вернулась из фона/сна: access-токен живёт 15 минут в памяти, а замороженная
// вкладка таймеры не крутит — после resume он почти всегда протух. Обновляем его ДО того,
// как проснутся поллинги (биллинг, тревоги, чат), иначе каждый ловит 401 и ретраит.
const refreshTokenOnResume = () => {
  if (document.hidden) return
  if (!hasRefreshableSession()) return
  if (getValidToken() && !shouldRefreshToken()) return
  silentRefresh()
}

// Боковое меню на body: оверлеи, телепортированные в body (модалка профиля,
// панель рисования геозоны), стоят с отступом под горизонтальную шапку. С боковым
// меню шапки нет, и отступ превращался в незатемнённую полосу сверху. Класс
// .app-shell--side-nav им не виден - он на оболочке внутри #app.
const syncSideNavBodyState = () => {
  document.body.classList.toggle('side-nav-active', sideNavActive.value)
}

const syncSplitLayoutBodyState = () => {
  const isSplitLayout = route.meta?.layout === 'split'
  const shouldLockBodyScroll = isSplitLayout && !isLoginPage.value && !isBlockedPage.value
  // Для split-страниц скролл должен жить во внутренних контейнерах, а не в window/document.
  document.body.classList.toggle('layout-split-active', shouldLockBodyScroll)
}

const updateTime = () => {
  currentTime.value = new Date().toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const handleScroll = () => {
  // Гістерезис: різні пороги для включення та вимкнення компактного режиму
  // Запобігає дрожанню при коливаннях scrollY біля порога
  const scrollY = window.scrollY
  if (isScrolled.value) {
    // Якщо вже в компактному режимі - вимикаємо тільки при scrollY < scrollThresholdUp
    if (scrollY < scrollThresholdUp) {
      isScrolled.value = false
    }
  } else {
    // Якщо в звичайному режимі - вмикаємо компактний при scrollY > scrollThresholdDown
    if (scrollY > scrollThresholdDown) {
      isScrolled.value = true
    }
  }
}

onMounted(() => {
  // Підписка на смену теми — оновлюємо іконку перемикача в шапці.
  unsubscribeTheme = subscribeTheme((resolved) => {
    currentTheme.value = resolved
  })
  unsubscribeNavLayout = subscribeNavLayout((layout) => {
    navLayout.value = layout
  })
  // Смена desktop/mobile по ширине окна - боковое меню только на десктопе
  desktopQuery?.addEventListener('change', onDesktopChange)

  // Сразу синхронизируем состояние body и подписываемся на смену layout/route.
  syncSplitLayoutBodyState()
  stopSplitLayoutWatcher = watch(
    () => [route.meta?.layout, isLoginPage.value, isBlockedPage.value],
    syncSplitLayoutBodyState
  )

  syncSideNavBodyState()
  stopSideNavWatcher = watch(sideNavActive, syncSideNavBodyState)

  // iOS Safari: при восстановлении suspended-вкладки GPU-слой .top-bar может быть потерян,
  // а sticky-позиционирование «уплывает». Агрессивный repaint: скрываем/показываем элемент,
  // чтобы браузер пересоздал compositing layer и пересчитал sticky-позицию.
  // На split-страницах (devices/maintenance/etc.) проблема другая: 100dvh у .app-shell
  // отдаёт устаревшее значение после suspend, и top-bar выталкивается за viewport вверх.
  // Решаем фиксацией пиксельной высоты на 1 кадр — браузер пересчитывает layout.
  const forceHeaderRepaint = () => {
    const topBar = document.querySelector('.top-bar')
    if (!topBar) return
    // Шаг 1: сбрасываем sticky → relative, чтобы вынуть из sticky-контекста
    topBar.style.position = 'relative'
    topBar.style.transform = 'translateZ(1px)'
    // Шаг 2: форсируем reflow чтением offsetHeight
    void topBar.offsetHeight
    // Шаг 3: возвращаем sticky и GPU-слой — браузер пересчитывает позицию с нуля
    requestAnimationFrame(() => {
      topBar.style.position = ''
      topBar.style.transform = ''
      // Шаг 4: корректируем scrollY — iOS может неверно его запомнить после freeze
      void topBar.offsetHeight
    })

    // На split-layout shell прибит через position:fixed inset:0 — viewport-математика не нужна.
    // Скидаємо лише scrollY: iOS після suspend інколи лишає ненульовий scrollY у window.
    if (document.body.classList.contains('layout-split-active')) {
      if (window.scrollY !== 0) window.scrollTo(0, 0)
      // iOS Safari: після resume fixed-контейнер втрачає hit-testing — кліки по
      // burger-меню та елементах sidebar не реєструються до наступної перерисовки.
      // Микро-тогл display форсує перерахунок stacking context і відновлює клики.
      const appShell = document.querySelector('.app-shell')
      if (appShell) {
        appShell.style.display = 'none'
        void appShell.offsetHeight
        appShell.style.display = ''
      }
    }
  }
  // Хак-репейнт нужен только на iOS Safari (suspend/resume вкладки, инерционный
  // скролл с URL-баром). На десктопе и Android scrollend срабатывает после
  // каждой остановки скролла, и переключение position sticky→relative→sticky
  // даёт видимое моргание шапки — поэтому ограничиваем хендлеры iOS.
  const ua = navigator.userAgent || ''
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1)
  if (isIos) {
    iosRepaintHandler = () => {
      if (!document.hidden) {
        // Двойной rAF: первый кадр — iOS отрисует «пустой» layout, второй — мы чиним
        requestAnimationFrame(() => requestAnimationFrame(forceHeaderRepaint))
      }
    }
    iosPageshowHandler = (e) => {
      if (e.persisted) requestAnimationFrame(() => requestAnimationFrame(forceHeaderRepaint))
    }
    document.addEventListener('visibilitychange', iosRepaintHandler)
    window.addEventListener('pageshow', iosPageshowHandler)
    // iOS Chrome/Safari: sticky-хедер может «отваливаться» после скрытия/появления URL-бара
    // (событие scrollend срабатывает в конце инерционного скролла) и после поворота экрана.
    // scrollend доступен в iOS 17+; в старых iOS просто не сработает — не критично.
    window.addEventListener('scrollend', iosRepaintHandler)
    window.addEventListener('orientationchange', iosRepaintHandler)
  }

  // Перевіряємо завантаження i18n кожні 50мс поки не завантажиться
  if (!i18nLoaded.value) {
    i18nCheckInterval = setInterval(() => {
      if (isI18nReady()) {
        i18nLoaded.value = true
        clearInterval(i18nCheckInterval)
        i18nCheckInterval = null
        // Оновлюємо title сторінки після завантаження i18n
        const brandName = t('brand.name')
        const titleKey = route.meta?.titleKey
        document.title = titleKey ? `${t(titleKey)} — ${brandName}` : brandName
      }
    }, 50)
  }
  // Оновлення часу
  updateTime()
  timeInterval = setInterval(updateTime, 10000)
  // Следим за сменой маршрутов и внешних изменений localStorage (другая вкладка).
  removeAfterEachHook = router.afterEach((to, from) => {
    const prevUser = currentUser.value
    refreshAuthState()
    closeMenu()
    closeUserMenu()
    closeNavMore()
    // Перезагружаем брендинг после логина или смены пользователя
    const newUser = currentUser.value
    if (newUser && (!prevUser || prevUser.id !== newUser.id)) {
      loadBranding()
    }
  })
  window.addEventListener('storage', refreshAuthState)
  document.addEventListener('click', handleDocumentClick)
  // Слушаем скролл для sticky header
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll() // Инициализируем состояние
  // На публичной странице шаринга весь клиент-портальский init пропускаем —
  // никаких загрузок tenant-логотипа, профиля, биллинга, баннеров, аналитики.
  if (isPublicPage.value) {
    return
  }
  // Возврат вкладки из фона/bfcache — сначала чиним токен, потом всё остальное.
  tokenResumeHandler = refreshTokenOnResume
  document.addEventListener('visibilitychange', tokenResumeHandler)
  tokenResumePageshowHandler = (e) => {
    if (e.persisted) refreshTokenOnResume()
  }
  window.addEventListener('pageshow', tokenResumePageshowHandler)
  // Заблокований клієнт живе тільки на /blocked, і бекенд усе одно віддає йому 403
  // на все, крім даних цієї сторінки. Тому фонові запити тут не запускаємо зовсім —
  // інакше вони просто стукали б у стіну кожні 30 секунд. Impersonate — повноцінна сесія.
  const blockedSession = isClientBlocked() && !isImpersonated()

  // Загружаем аватар пользователя и логотип tenant для брендинга.
  loadBranding()
  // Загружаем статус биллинга и баннеры
  loadBillingStatus()
  // Фіксуємо відвідування (відкриття вкладки з живим токеном) для аналітики логінів
  if (isAuthenticated() && !blockedSession) {
    reportVisit()
  }
  // Запускаем onboarding при первом входе
  startOnboardingIfNeeded()
  if (!blockedSession) {
    // Глобальний alarm polling: звук/сповіщення тривог працюють на всіх сторінках портала,
    // включно з фоновою вкладкою (браузер сам троттлить інтервал до ~1/хв).
    // Без токена (сторінка логіна) checkAlarms просто нічого не робить.
    alarmState.startPolling()
    // Бейдж підтримки: WS-poke (через user-канал) + рідкий поллінг; без токена — no-op
    startSupportUnread()
    // Лічильник непрочитаних повідомлень від водіїв/персоналу — теж глобальний,
    // щоб бейдж у меню оновлювався на будь-якій сторінці. Без токена — no-op.
    startChatPolling()
  }
  // Опрашиваем баннеры и статус каждые 30 секунд для появления без перезагрузки
  billingPollInterval = setInterval(() => {
    if (isAuthenticated()) {
      loadBillingStatus()
    }
  }, 30000)

  // Обрабатываем redirect после impersonate авторизации
  const impersonateRedirect = sessionStorage.getItem('impersonate_redirect')
  if (impersonateRedirect && isAuthenticated()) {
    sessionStorage.removeItem('impersonate_redirect')
    router.push(impersonateRedirect)
  }
})

onBeforeUnmount(() => {
  alarmState.stopPolling()
  stopSupportUnread()
  stopChatPolling()
  if (unsubscribeTheme) {
    unsubscribeTheme()
    unsubscribeTheme = null
  }
  if (unsubscribeNavLayout) {
    unsubscribeNavLayout()
    unsubscribeNavLayout = null
  }
  desktopQuery?.removeEventListener('change', onDesktopChange)
  if (stopSplitLayoutWatcher) {
    stopSplitLayoutWatcher()
  }
  if (stopSideNavWatcher) {
    stopSideNavWatcher()
  }
  document.body.classList.remove('layout-split-active')
  document.body.classList.remove('side-nav-active')
  if (removeAfterEachHook) {
    removeAfterEachHook()
  }
  window.removeEventListener('storage', refreshAuthState)
  document.removeEventListener('click', handleDocumentClick)
  window.removeEventListener('scroll', handleScroll)
  navResizeObserver?.disconnect()
  if (navMeasureFrame) cancelAnimationFrame(navMeasureFrame)
  if (billingPollInterval) {
    clearInterval(billingPollInterval)
  }
  stopEmailPoll()
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (timeInterval) {
    clearInterval(timeInterval)
  }
  if (i18nCheckInterval) {
    clearInterval(i18nCheckInterval)
  }
  if (iosRepaintHandler) {
    document.removeEventListener('visibilitychange', iosRepaintHandler)
    window.removeEventListener('scrollend', iosRepaintHandler)
    window.removeEventListener('orientationchange', iosRepaintHandler)
  }
  if (iosPageshowHandler) {
    window.removeEventListener('pageshow', iosPageshowHandler)
  }
  if (tokenResumeHandler) {
    document.removeEventListener('visibilitychange', tokenResumeHandler)
    tokenResumeHandler = null
  }
  if (tokenResumePageshowHandler) {
    window.removeEventListener('pageshow', tokenResumePageshowHandler)
    tokenResumePageshowHandler = null
  }
})
</script>

<template>
  <!-- Loader поки завантажується i18n -->
  <div v-if="!i18nLoaded" class="app-loader">
    <div class="app-loader__spinner"></div>
  </div>

  <div
    v-else
    class="app-shell"
    :class="{
      'app-shell--auth': isLoginPage || isPublicPage,
      'app-shell--side-nav': sideNavActive,
      'app-shell--side-nav-icons': sideNavIconsOnly,
    }"
  >
    <header v-if="!isPublicPage" ref="topBarRef" class="top-bar" :class="{ 'top-bar--auth': isLoginPage, 'top-bar--scrolled': isScrolled && !isLoginPage && !isBlockedPage }">
      <div class="top-bar__left">
        <button
          v-if="!isLoginPage"
          class="menu-toggle"
          type="button"
          :aria-expanded="isMenuOpen"
          aria-label="Toggle navigation"
          @click="toggleMenu"
        >
          <span class="menu-toggle__line"></span>
          <span class="menu-toggle__line"></span>
          <span class="menu-toggle__line"></span>
          <!-- Лічильник непрочитаних відповідей підтримки (пункт «Заявки» в сайдбарі) -->
          <span v-if="supportUnreadCount > 0 && !currentUser?.isSubUser" class="support-unread-badge support-unread-badge--pin" aria-hidden="true">
            {{ supportUnreadCount > 99 ? '99+' : supportUnreadCount }}
          </span>
        </button>
        <!-- Mobile notification bell (якщо є доступ до білінгу або системних повідомлень) -->
        <NotificationBell
          v-if="currentUser && !isLoginPage && !isBlockedPage && (hasBillingAccess() || hasMessagesAccess())"
          :show-billing-tab="hasBillingAccess()"
          :show-system-tab="hasMessagesAccess()"
          class="notification-bell--mobile"
          @open-campaign="openCampaign"
        />
        <!-- Швидке перемикання теми — версія для мобільного -->
        <button
          v-if="!isLoginPage && !isBlockedPage"
          type="button"
          class="theme-toggle theme-toggle--mobile"
          :aria-label="currentTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
          @click="toggleTheme"
        >
          <span class="material-symbols-outlined" aria-hidden="true">
            {{ currentTheme === 'dark' ? 'light_mode' : 'dark_mode' }}
          </span>
        </button>
      </div>

      <div ref="brandBlockRef" class="brand-block">
        <!-- Логотип дилера -->
        <img v-if="dealerLogoUrl" :src="dealerLogoUrl" alt="Logo" :class="['brand-logo', { 'brand-logo--cloudgps-default': isDefaultCloudgpsLogo }]" />
        <template v-else>
          <div class="brand">{{ t('brand.name') }}</div>
          <p class="tagline">{{ t('brand.tagline') }}</p>
        </template>
      </div>

      <div v-if="!isLoginPage && !isBlockedPage" ref="topBarActionsRef" class="top-bar__actions">
        <!-- data-tour="main-nav" - якорь шага онбординга; ставится на ту навигацию,
             которая видна в текущем режиме (верхняя или боковая колонка) -->
        <nav ref="navDesktopRef" class="nav nav--desktop" aria-label="Main navigation" :data-tour="sideNavActive ? null : 'main-nav'">
          <RouterLink v-for="link in visibleNavLinks" :key="link.to" :to="link.to" class="nav__link" @click="handleNavClick(link, $event)">
            {{ t(link.labelKey) }}
            <!-- Непрочитані повідомлення від водіїв/персоналу -->
            <span v-if="navUnread(link) > 0" class="support-unread-badge">
              {{ navUnread(link) > 99 ? '99+' : navUnread(link) }}
            </span>
          </RouterLink>
          <!-- Пункты, которые не влезли в шапку -->
          <div v-if="overflowNavLinks.length" ref="navMoreRef" class="nav-more">
            <button
              type="button"
              class="nav__link nav-more__trigger"
              :class="{ 'router-link-active': isNavMoreActive }"
              aria-haspopup="menu"
              :aria-expanded="isNavMoreOpen"
              @click.stop="toggleNavMore"
            >
              {{ t('nav.more') }}
              <span class="user-menu__caret" aria-hidden="true"></span>
              <span v-if="navMoreUnread > 0" class="support-unread-badge support-unread-badge--pin" aria-hidden="true">
                {{ navMoreUnread > 99 ? '99+' : navMoreUnread }}
              </span>
            </button>
            <div v-if="isNavMoreOpen" class="nav-more__panel" role="menu">
              <RouterLink
                v-for="link in overflowNavLinks"
                :key="link.to"
                :to="link.to"
                class="nav-more__link"
                role="menuitem"
                @click="handleNavClick(link, $event); closeNavMore()"
              >
                <span class="material-symbols-outlined nav__icon" aria-hidden="true">{{ link.icon }}</span>
                <span class="nav__label">{{ t(link.labelKey) }}</span>
                <span v-if="navUnread(link) > 0" class="support-unread-badge">
                  {{ navUnread(link) > 99 ? '99+' : navUnread(link) }}
                </span>
              </RouterLink>
            </div>
          </div>
          <!-- Невидимый ряд-образец со всеми пунктами и кнопкой «Ещё»: по нему measureNavFit
               считает, сколько пунктов влезает. Те же классы, что у живых пунктов, поэтому
               сжатие по ширине экрана и компактная шапка при скролле действуют и на него -->
          <div class="nav-measure" aria-hidden="true">
            <div ref="navMeasureRef" class="nav-measure__row">
              <span v-for="link in navLinks" :key="link.to" class="nav__link">
                {{ t(link.labelKey) }}
                <span v-if="navUnread(link) > 0" class="support-unread-badge">
                  {{ navUnread(link) > 99 ? '99+' : navUnread(link) }}
                </span>
              </span>
              <span class="nav__link nav-more__trigger">
                {{ t('nav.more') }}
                <span class="user-menu__caret"></span>
              </span>
            </div>
          </div>
        </nav>
        <!-- Часы, тема, колокольчик и меню пользователя рендерятся в сайдбаре (один экземпляр)
             и телепортируются сюда, пока меню сверху; в боковом режиме остаются внизу колонки -->
        <div class="top-bar__slot"></div>
      </div>
    </header>

    <!-- Баннеры уведомлений с show_as_banner -->
    <WarningBanner
      v-for="banner in visibleBanners"
      :key="banner.id"
      :variant="getBannerVariant(banner.type)"
      :notification-type="banner.type"
      :title="banner.title"
      :message="banner.message"
      :dismissible="true"
      @dismiss="dismissBanner(banner.id)"
      @click="openBannerModal(banner)"
    />
    <!-- Fallback баннер статуса биллинга если нет API баннеров -->
    <WarningBanner
      v-if="showBillingStatusBanner"
      :variant="billingStatusBannerVariant"
      :title="billingStatusBannerTitle"
      :message="billingStatusBannerMessage"
      :dismissible="!isClientSuspended()"
      @dismiss="dismissBanner('billing-status')"
    />
    <!-- Баннер: email не вказано — потрібен для відновлення пароля -->
    <WarningBanner
      v-if="showEmailBanner"
      variant="info"
      :message="t('emailBanner.message')"
      :dismissible="true"
      @dismiss="emailBannerDismissed = true"
      @click="openSettingsForEmail"
    />

    <div class="shell-grid" :class="{ 'shell-grid--auth': isLoginPage || isPublicPage }">
      <aside v-if="!isLoginPage && !isBlockedPage && !isPublicPage" class="sidebar" :class="{ 'sidebar--open': isMenuOpen }">
        <div class="sidebar__inner">
          <!-- Боковой режим: шапка скрыта, логотип показываем в верху колонки -->
          <div v-if="sideNavActive" class="sidebar__brand">
            <img v-if="dealerLogoUrl" :src="dealerLogoUrl" alt="Logo" :class="['brand-logo', { 'brand-logo--cloudgps-default': isDefaultCloudgpsLogo }]" />
            <span v-else class="brand">{{ t('brand.name') }}</span>
          </div>
          <p class="eyebrow">{{ t('nav.sectionLabel') }}</p>
          <!-- Мобильная шторка; на десктопе в режимах side/side-icons - боковая колонка -->
          <nav class="nav nav--drawer" aria-label="Mobile navigation" :data-tour="sideNavActive ? 'main-nav' : null">
            <RouterLink
              v-for="link in navLinks"
              :key="link.to"
              :to="link.to"
              class="nav__link"
              :title="sideNavIconsOnly ? t(link.labelKey) : null"
              :aria-label="sideNavIconsOnly ? t(link.labelKey) : null"
              @click="handleNavClick(link, $event); closeMenu()"
            >
              <!-- Иконка пункта: в шторке и в боковых режимах меню на десктопе -->
              <span class="material-symbols-outlined nav__icon" aria-hidden="true">{{ link.icon }}</span>
              <span class="nav__label">{{ t(link.labelKey) }}</span>
              <span v-if="navUnread(link) > 0" class="support-unread-badge">
                {{ navUnread(link) > 99 ? '99+' : navUnread(link) }}
              </span>
            </RouterLink>
          </nav>
          <div v-if="currentUser" class="sidebar__user">
            <div class="sidebar__user-info">
              <span class="material-symbols-outlined" aria-hidden="true">person</span>
              <span class="sidebar__user-name">{{ currentUser.username }}</span>
            </div>
            <!-- Суб-пользователю настройки тоже доступны, но только вид (язык, тема, текст, панель) -->
            <button class="sidebar__settings-btn" type="button" @click="openSettingsFromSidebar">
              <span class="material-symbols-outlined" aria-hidden="true">settings</span>
              {{ t('nav.settings') }}
            </button>
            <button v-if="hasBillingAccess()" class="sidebar__settings-btn" type="button" @click="openBillingFromSidebar">
              <span class="material-symbols-outlined" aria-hidden="true">receipt_long</span>
              {{ t('nav.billing') }}
            </button>
            <!-- Временно скрыто — notifications -->
            <button v-if="false" class="sidebar__settings-btn" type="button" @click="openNotificationsFromSidebar">
              <span class="material-symbols-outlined" aria-hidden="true">notifications</span>
              {{ t('nav.notifications') }}
            </button>
            <button v-if="hasRequestsAccess()" class="sidebar__settings-btn" type="button" @click="openRequestsFromSidebar">
              <span class="material-symbols-outlined" aria-hidden="true">assignment</span>
              {{ t('nav.requests') }}
              <span v-if="supportUnreadCount > 0" class="support-unread-badge">
                {{ supportUnreadCount > 99 ? '99+' : supportUnreadCount }}
              </span>
            </button>
            <button class="sidebar__settings-btn" type="button" @click="openWhatsNewFromSidebar">
              <span class="material-symbols-outlined" aria-hidden="true">campaign</span>
              {{ t('nav.whatsNew') }}
              <span v-if="hasUnreadNews" class="news-dot" aria-hidden="true"></span>
            </button>
            <button class="sidebar__settings-btn" type="button" @click="openHelpFromSidebar">
              <span class="material-symbols-outlined" aria-hidden="true">help_outline</span>
              {{ t('nav.help') }}
            </button>
          </div>
          <button v-if="authenticated" class="logout logout--full" type="button" @click="handleLogout">
            {{ t('nav.logout') }}
          </button>
          <!-- Часы/тема/колокольчик и меню пользователя: в боковом режиме живут внизу колонки,
               иначе телепортируются в шапку (.top-bar__slot). Один экземпляр - состояние меню и
               обработчики не дублируются. defer: цель в шапке идёт раньше по DOM, но при первом
               монтировании оболочки её ещё нет в документе. -->
          <Teleport defer to=".top-bar__slot" :disabled="sideNavActive">
            <!-- Час та колокольчик справа -->
            <div class="top-bar__controls">
              <span class="control-time">{{ currentTime }}</span>
              <!-- Быстрое переключение Light/Dark (без сохранения, только текущий сеанс) -->
              <button
                type="button"
                class="theme-toggle"
                :aria-label="currentTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
                @click="toggleTheme"
              >
                <span class="material-symbols-outlined" aria-hidden="true">
                  {{ currentTheme === 'dark' ? 'light_mode' : 'dark_mode' }}
                </span>
              </button>
              <NotificationBell
                v-if="currentUser && (hasBillingAccess() || hasMessagesAccess())"
                :show-billing-tab="hasBillingAccess()"
                :show-system-tab="hasMessagesAccess()"
                @open-campaign="openCampaign"
              />
            </div>
            <!-- Делаем доступ ко входу и выходу через компактное меню, чтобы хедер был чище. -->
            <div v-if="currentUser" class="user-menu" ref="userMenuRef">
              <button
                class="user-menu__trigger"
                type="button"
                aria-haspopup="menu"
                :aria-expanded="isUserMenuOpen"
                :aria-label="t('nav.userMenuToggle')"
                @click.stop="toggleUserMenu"
              >
                <!-- Аватар пользователя или иконка по умолчанию -->
                <img v-if="userAvatarUrl" :src="userAvatarUrl" alt="Avatar" class="user-menu__avatar-img" />
                <span v-else class="material-symbols-outlined user-menu__avatar" aria-hidden="true">person</span>
                <span class="user-menu__username">{{ currentUser.username }}</span>
                <span class="user-menu__caret" aria-hidden="true"></span>
                <!-- Крапка «є нова новина» (лівий верхній кут, щоб не перекривати лічильник підтримки) -->
                <span v-if="hasUnreadNews" class="news-dot news-dot--pin" aria-hidden="true"></span>
                <!-- Лічильник непрочитаних відповідей підтримки — видно без відкриття меню -->
                <span v-if="supportUnreadCount > 0 && !currentUser?.isSubUser" class="support-unread-badge support-unread-badge--pin" aria-hidden="true">
                  {{ supportUnreadCount > 99 ? '99+' : supportUnreadCount }}
                </span>
              </button>
              <div v-if="isUserMenuOpen" class="user-menu__panel" role="menu">
                <button
                  class="user-menu__action"
                  type="button"
                  role="menuitem"
                  @click="openSettings"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">settings</span>
                  {{ t('nav.settings') }}
                </button>
                <button
                  v-if="hasBillingAccess()"
                  class="user-menu__action"
                  type="button"
                  role="menuitem"
                  @click="openBilling"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">receipt_long</span>
                  {{ t('nav.billing') }}
                </button>
                <!-- Временно скрыто — notifications -->
                <button
                  v-if="false"
                  class="user-menu__action"
                  type="button"
                  role="menuitem"
                  @click="openNotifications"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">notifications</span>
                  {{ t('nav.notifications') }}
                </button>
                <button
                  v-if="hasRequestsAccess()"
                  class="user-menu__action"
                  type="button"
                  role="menuitem"
                  @click="openRequests"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">assignment</span>
                  {{ t('nav.requests') }}
                  <span v-if="supportUnreadCount > 0" class="support-unread-badge">
                    {{ supportUnreadCount > 99 ? '99+' : supportUnreadCount }}
                  </span>
                </button>
                <button
                  class="user-menu__action"
                  type="button"
                  role="menuitem"
                  @click="openWhatsNew"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">campaign</span>
                  {{ t('nav.whatsNew') }}
                  <span v-if="hasUnreadNews" class="news-dot" aria-hidden="true"></span>
                </button>
                <button
                  class="user-menu__action user-menu__action--help"
                  type="button"
                  role="menuitem"
                  @click="openHelp"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">help_outline</span>
                  {{ t('nav.help') }}
                </button>
                <button
                  class="logout user-menu__logout"
                  type="button"
                  role="menuitem"
                  @click="handleLogout"
                >
                  {{ t('nav.logout') }}
                </button>
              </div>
            </div>
          </Teleport>
          <!-- Десктоп, боковые режимы: свернуть до иконок / развернуть (side <-> side-icons).
               На мобильном скрыта через CSS -->
          <button
            class="sidebar__collapse"
            type="button"
            :title="navIconsOnly ? t('settings.navLayout.side') : t('settings.navLayout.sideIcons')"
            :aria-label="navIconsOnly ? t('settings.navLayout.side') : t('settings.navLayout.sideIcons')"
            @click="toggleSideNav"
          >
            <span class="material-symbols-outlined" aria-hidden="true">
              {{ navIconsOnly ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left' }}
            </span>
          </button>
        </div>
      </aside>

      <main class="content" :class="{ 'content--auth': isLoginPage || isPublicPage }">
        <!-- Рендерим страницу через layout-router, чтобы layout выбирался по meta.layout -->
        <ClientLayoutRouter />
      </main>
    </div>

    <button
      v-if="isMenuOpen && !isLoginPage"
      class="backdrop"
      type="button"
      @click="closeMenu"
      aria-label="Close menu"
    ></button>

    <UserSettingsModal
      :open="isSettingsOpen"
      :display-only="!!currentUser?.isSubUser"
      @close="closeSettings"
      @updated="onProfileUpdated"
    />

    <!-- Модалка для полного текста баннера -->
    <NotificationModal
      :open="!!selectedBannerForModal"
      :notification="selectedBannerForModal"
      @close="closeBannerModal"
    />

    <!-- Пропозиція дилера: окно при входе, из колокольчика и по ссылке из push -->
    <CampaignModal
      :open="campaignModal.open"
      :campaign-id="campaignModal.id"
      :preloaded="campaignModal.preloaded"
      :from-popup="campaignModal.fromPopup"
      @close="closeCampaign"
    />

    <!-- Система справки -->
    <HelpPanel />

    <!-- Предложение установки: на своих хостах приложение из магазина, у партнёров PWA.
         Не на странице подключения телефона: там ставят приложение-трекер из Google Play -->
    <PwaInstallPrompt v-if="route.name !== 'tracker-pair'" :store-offer="!isPublicPage" />

    <!-- Внутри приложения: один раз предложить уведомления -->
    <AppPushOffer :active="authenticated && !isLoginPage && !isBlockedPage && !isPublicPage" />

    <!-- Анімований гайд встановлення PWA на iPhone -->
    <IosInstallGuide />

    <!-- Баннер отсутствия сети -->
    <OfflineBanner />

    <!-- Переписка з водієм/персоналом: вікно живе поверх усіх сторінок,
         доки користувач сам його не закриє -->
    <DriverChatWindow />
  </div>
</template>

<style scoped>
/* Loader під час завантаження i18n */
.app-loader {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-card);
  z-index: 9999;
}

.app-loader__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-default);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
